<script lang="ts" setup>
	import { ref, watch, onMounted } from 'vue';
	import { useTeamStore } from '@/store/team.store';
	import { IGroup } from '@/types';
	import { useModalStore } from '@/store/modal.store';
	import DefaultBtn from "@/components/block/DefaultBtn.vue";
	import { useRouter } from 'vue-router';

	const router = useRouter();
	const modalStore = useModalStore();
	const teamStore = useTeamStore();

	const props = defineProps<{
		closeSignal?: boolean
	}>();

	watch(() => props.closeSignal, () => {
		if (props.closeSignal) {
			close();
		}
	});

	const newCompany = ref<IGroup>({
		id: '',
		type: 'insurance',
		root: '',
		name: '',
		activeSubscribers: Date.now(),
		created: Date.now(),
		reportUploadedDate: 0,
		active: true,
		email: '',
		phone: '',
		deviceOption: 'single',
		billing: {
			name: '',
			middleName: '',
			lastName: '',
			department: '',
			title: '',
			phone: '',
			email: '',
			billingEmail: '',
		},
	});
	const currentStep = ref<string>('step1');
	const previousStep = ref<string | null>(null);
	const nameError = ref<string>("");
	const emailError = ref<string>("");
	const phoneError = ref<string>("");
	const billingNameError = ref<string>("");
	const billingMiddleNameError = ref<string>("");
	const billingLastNameError = ref<string>("");
	const billingDepartmentError = ref<string>("");
	const billingTitleError = ref<string>("");
	const billingPhoneError = ref<string>("");
	const billingEmailError = ref<string>("");

	const realoarGroup = (): void => {
		newCompany.value.id = '';
		newCompany.value.name = '';
		newCompany.value.email = '';
		newCompany.value.phone = '';
		newCompany.value.billing.name = '';
		newCompany.value.billing.middleName = '';
		newCompany.value.billing.lastName = '';
		newCompany.value.billing.department = '';
		newCompany.value.billing.title = '';
		newCompany.value.billing.phone = '';
		newCompany.value.billing.email = '';
		step1();
	};

	watch(() => newCompany.value.phone, (current, prev) => {
		if (current !== prev && newCompany.value.phone) {
			const digitsOnly = newCompany.value.phone.replace(/\D/g, '');
			newCompany.value.phone = ('+1' + digitsOnly.replace(/^1/, '')).slice(0, 12);
		}
	})

	watch(() => newCompany.value.billing.phone, (current, prev) => {
		if (current !== prev && newCompany.value.billing.phone) {
			const digitsOnly = newCompany.value.billing.phone.replace(/\D/g, '');
			newCompany.value.billing.phone = ('+1' + digitsOnly.replace(/^1/, '')).slice(0, 12);
		}
	})
	
	const loading = ref<boolean>(false);
	const createGroup = (): void => {
		if (!loading.value) {
			loading.value = true;

			teamStore.createGroup(newCompany.value)
				.then((id) => {
					newCompany.value.id = id;
					step5();
				})
				.finally(() => {
					loading.value = false;
				});
		}
	};

	const openGroup = (): void => {
		router.push('companies/' + newCompany.value.id)
		modalStore.closeModal();
	};

	const close = (): void => {
		if (!loading.value) {
			modalStore.closeModal();
		}
	};

	const validateStep1 = () => {
		let isValid = true;
		if (!newCompany.value.name?.length) {
			nameError.value = "Fill in the field";
			isValid = false;
		} else {
			nameError.value = "";
		}
		if (isValid) {
			step2();
		}
	};

	const validateStep2 = () => {
		let isValid = true;

		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!newCompany.value.email || !emailPattern.test(newCompany.value.email)) {
			emailError.value = "Please enter a valid email";
			isValid = false;
		} else {
			emailError.value = "";
		}

		if (!newCompany.value.phone || newCompany.value.phone.length < 10 || !(/^\+1\d{10}$/.test(newCompany.value.phone))) {
			phoneError.value = "Please enter a valid phone number";
			isValid = false;
		} else {
			phoneError.value = "";
		}

		if (isValid) {
			step3();
		}
	};

	const validateStep3 = () => {
		let isValid = true;

		if (!newCompany.value.billing.name?.length) {
			billingNameError.value = "Fill in the field";
			isValid = false;
		} else {
			billingNameError.value = "";
		}

		if (!newCompany.value.billing.middleName?.length) {
			billingMiddleNameError.value = "Fill in the field";
			isValid = false;
		} else {
			billingMiddleNameError.value = "";
		}

		if (!newCompany.value.billing.lastName?.length) {
			billingLastNameError.value = "Fill in the field";
			isValid = false;
		} else {
			billingLastNameError.value = "";
		}

		if (!newCompany.value.billing.department?.length) {
			billingDepartmentError.value = "Fill in the field";
			isValid = false;
		} else {
			billingDepartmentError.value = "";
		}

		if (isValid) {
			step4();
		}
	};

	const validateStep4 = async () => {
		let isValid = true;

		if (!newCompany.value.billing.title || !newCompany.value.billing.title.length) {
			billingTitleError.value = "Fill in the field";
			isValid = false;
		} else {
			billingTitleError.value = "";
		}

		if (newCompany.value.billing.phone) {
			const digitsOnly = newCompany.value.billing.phone.replace(/\D/g, '');
			newCompany.value.billing.phone = ('+1' + digitsOnly.replace(/^1/, '')).slice(0, 12);
		}

		if (!newCompany.value.billing.phone || !newCompany.value.billing.phone.length) {
			billingPhoneError.value = "Fill in the field";
			isValid = false;
		} else {
			billingPhoneError.value = "";
		}

		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!newCompany.value.billing.email || !emailPattern.test(newCompany.value.billing.email)) {
			billingEmailError.value = "Please enter a valid email";
			isValid = false;
		} else {
			billingEmailError.value = "";
		}

		return isValid;
	};

	async function handleContinue() {
		const isValid = await validateStep4();
		if (isValid) {
			createGroup();
		}
	}

	function step1() {
  modalStore.closeModal();
  modalStore.openModal('CompanyOnboarding');
  currentStep.value = 'step1';
}

function step2() {
  modalStore.closeModal();
  modalStore.openModal('step2');
  currentStep.value = 'step2';
}

function step3() {
  modalStore.closeModal();
  modalStore.openModal('step3');
  currentStep.value = 'step3';
}

function step4() {
  modalStore.closeModal();
  modalStore.openModal('step4');
  currentStep.value = 'step4';
}

function step5() {
  modalStore.closeModal();
  modalStore.openModal('step5');
  currentStep.value = 'step5';
}

function step6() {
  previousStep.value = currentStep.value;
  modalStore.closeModal();
  modalStore.openModal('step6');
  currentStep.value = 'step6';
}

function goBackToPreviousStep() {
  if (previousStep.value) {
    modalStore.closeModal();
    currentStep.value = previousStep.value;
    previousStep.value = null;

    if (currentStep.value === 'step1') {
      step1();
    } else if (currentStep.value === 'step2') {
      step2();
    } else if (currentStep.value === 'step3') {
      step3();
    } else if (currentStep.value === 'step4') {
      step4();
    } else if (currentStep.value === 'step5') {
      step5();
    }
  } else {
    step1();
  }
}

	onMounted(() => {
  window.addEventListener("keydown", handleEnterKey);
});

const handleEnterKey = (e: KeyboardEvent) => {
  const steps = [
    { selector: '.step1', validate: validateStep1 },
    { selector: '.step2', validate: validateStep2 },
    { selector: '.step3', validate: validateStep3 },
    { selector: '.step4', validate: handleContinue }

  ];

  steps.forEach(step => {
    const loginForm = document.querySelector(step.selector);
    if (e.key === "Enter" && loginForm) {
      step.validate();
    }
  });
};


</script>

<template>
	<div v-if="modalStore.isModalVisible('CompanyOnboarding')" class="modal modal--active step1">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">corporate_fare</span>
					<span>Company Onboarding</span>
				</p>
				<button @click="step6" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<form @submit.prevent="validateStep1">
					<div class="login__item">
						<input
							v-model="newCompany.name"
							class="input"
							placeholder="Company Name*"
						/>
						<span class="material-symbols-outlined login__item_ico">text_fields_alt</span>
						<p class="error" v-if="nameError">{{ nameError }}</p>
					</div>
					<button class="login__btn" type="submit" >Continue</button>
			</form>
			</div>
		</div>
	</div>

	<div v-else-if="modalStore.isModalVisible('step2')" class="modal modal--profile modal--active step2">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">support_agent</span>
					<span>Customer Communications:</span>
				</p>
				<button @click="step6" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<form @submit.prevent="validateStep2">
					<div class="login__item">
						<input
							v-model="newCompany.email"
							class="input"
							placeholder="CC. Email*"
						/>
						<span class="material-symbols-outlined login__item_ico">mail</span>
						<p class="error" v-if="emailError">{{ emailError }}</p>
					</div>
					<div class="login__item">
						<input
							v-model="newCompany.phone"
							class="input"
							placeholder="CC. Phone Number*"
						/>
						<span class="material-symbols-outlined login__item_ico">phone</span>
						<p class="error" v-if="phoneError">{{ phoneError }}</p>
					</div>
					<div class="btn__wrap">
						<button class="login__btn btn--white" type="button" @click="step1">Previous</button>
						<button class="login__btn" type="submit" >Continue</button>
					</div>
				</form>	
			</div>
		</div>
	</div>

	<div v-else-if="modalStore.isModalVisible('step3')" class="modal modal--profile modal--active step3">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">request_quote</span>
					<span>Billing Contact Info:</span>
				</p>
				<button @click="step6" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<form @submit.prevent="validateStep3">
					<div class="login__item">
						<input v-model="newCompany.billing.name" class="input" placeholder="First Name" />
						<span class="material-symbols-outlined login__item_ico">person</span>
						<p class="error" v-if="billingNameError">{{ billingNameError }}</p>
					</div>

					<div class="login__item">
						<input v-model="newCompany.billing.middleName" class="input" placeholder="Middle Name" />
						<span class="material-symbols-outlined login__item_ico">person</span>
						<p class="error" v-if="billingMiddleNameError">{{ billingMiddleNameError }}</p>
					</div>

					<div class="login__item">
						<input v-model="newCompany.billing.lastName" class="input" placeholder="Last Name" />
						<span class="material-symbols-outlined login__item_ico">person</span>
						<p class="error" v-if="billingLastNameError">{{ billingLastNameError }}</p>
					</div>

					<div class="login__item">
						<input v-model="newCompany.billing.department" class="input" placeholder="Department" />
						<span class="material-symbols-outlined login__item_ico">corporate_fare</span>
						<p class="error" v-if="billingDepartmentError">{{ billingDepartmentError }}</p>
					</div>

					<div class="btn__wrap">
						<button class="login__btn btn--white" type="button" @click="step2">Previous</button>
						<button class="login__btn" type="submit" >Continue</button>
					</div>
				</form>
			</div>
		</div>
	</div>

	<div v-if="modalStore.isModalVisible('step4')" class="modal modal--profile modal--active step4">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">request_quote</span>
					<span>Billing Contact Info:</span>
				</p>
				<button @click="step6" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<form @submit.prevent="handleContinue">
					<div class="login__item">
						<input v-model="newCompany.billing.title" class="input" placeholder="Title" />
						<span class="material-symbols-outlined login__item_ico">title</span>
						<p class="error" v-if="billingTitleError">{{ billingTitleError }}</p>
					</div>

					<div class="login__item">
						<input v-model="newCompany.billing.phone" class="input" placeholder="Phone" />
						<span class="material-symbols-outlined login__item_ico">phone</span>
						<p class="error" v-if="billingPhoneError">{{ billingPhoneError }}</p>
					</div>

					<div class="login__item">
						<input v-model="newCompany.billing.email" class="input" placeholder="Email" />
						<span class="material-symbols-outlined login__item_ico">mail</span>
						<p class="error" v-if="billingEmailError">{{ billingEmailError }}</p>
					</div>

					<div class="login__item">
						<input v-model="newCompany.billing.billingEmail" class="input" placeholder="Billing Email (if applicable)" />
						<span class="material-symbols-outlined login__item_ico">mail</span>
					</div>

					<div class="btn__wrap">
						<DefaultBtn class="login__btn btn--white" :neutral="true" text="Previous" @click="step3" />
						<DefaultBtn class="login__btn" :loading text="Finish"type="submit" />
					</div>
				</form>
			</div>
		</div>
	</div>

	<div v-else-if="modalStore.isModalVisible('step5')" class="modal modal--profile modal--active step5">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">corporate_fare</span>
					<span>Company Onboarding</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<svg class="modal__update_svg" width="94" height="94" viewBox="0 0 94 94" fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						d="M40.0532 57.505L28.6823 46.1341C28.0046 45.4564 27.1527 45.1096 26.1267 45.094C25.1007 45.0782 24.2331 45.425 23.524 46.1341C22.815 46.8432 22.4604 47.7029 22.4604 48.7132C22.4604 49.7234 22.815 50.5831 23.524 51.2922L36.9563 64.7245C37.8411 65.6093 38.8734 66.0517 40.0532 66.0517C41.233 66.0517 42.2652 65.6093 43.15 64.7245L70.3817 37.4928C71.0595 36.8151 71.4062 35.9633 71.4219 34.9373C71.4376 33.9112 71.0909 33.0437 70.3817 32.3346C69.6726 31.6255 68.813 31.271 67.8027 31.271C66.7924 31.271 65.9327 31.6255 65.2236 32.3346L40.0532 57.505ZM47.0082 93.5C40.5768 93.5 34.5316 92.2796 28.8727 89.8387C23.2136 87.3979 18.291 84.0854 14.105 79.9012C9.9188 75.7169 6.60477 70.7965 4.16286 65.1399C1.72096 59.4835 0.5 53.4396 0.5 47.0082C0.5 40.5768 1.72042 34.5316 4.16127 28.8727C6.60212 23.2136 9.91464 18.291 14.0988 14.105C18.2831 9.91881 23.2035 6.60478 28.8601 4.16287C34.5165 1.72096 40.5604 0.5 46.9918 0.5C53.4232 0.5 59.4684 1.72043 65.1273 4.16128C70.7864 6.60213 75.709 9.91464 79.895 14.0988C84.0812 18.2831 87.3952 23.2035 89.8371 28.8601C92.279 34.5165 93.5 40.5604 93.5 46.9918C93.5 53.4232 92.2796 59.4683 89.8387 65.1273C87.3979 70.7864 84.0854 75.709 79.9012 79.895C75.7169 84.0812 70.7965 87.3952 65.1399 89.8371C59.4835 92.279 53.4396 93.5 47.0082 93.5ZM47 86.158C57.9316 86.158 67.1908 82.3646 74.7777 74.7777C82.3646 67.1908 86.158 57.9316 86.158 47C86.158 36.0684 82.3646 26.8092 74.7777 19.2223C67.1908 11.6354 57.9316 7.842 47 7.842C36.0684 7.842 26.8092 11.6354 19.2223 19.2223C11.6354 26.8092 7.842 36.0684 7.842 47C7.842 57.9316 11.6354 67.1908 19.2223 74.7777C26.8092 82.3646 36.0684 86.158 47 86.158Z"
						fill="#2563EB" />
					<path
						d="M40.0532 57.505L28.6823 46.1341C28.0046 45.4564 27.1527 45.1096 26.1267 45.094C25.1007 45.0782 24.2331 45.425 23.524 46.1341C22.815 46.8432 22.4604 47.7029 22.4604 48.7132C22.4604 49.7234 22.815 50.5831 23.524 51.2922L36.9563 64.7245C37.8411 65.6093 38.8734 66.0517 40.0532 66.0517C41.233 66.0517 42.2652 65.6093 43.15 64.7245L70.3817 37.4928C71.0595 36.8151 71.4062 35.9633 71.4219 34.9373C71.4376 33.9112 71.0909 33.0437 70.3817 32.3346C69.6726 31.6255 68.813 31.271 67.8027 31.271C66.7924 31.271 65.9327 31.6255 65.2236 32.3346L40.0532 57.505ZM47.0082 93.5C40.5768 93.5 34.5316 92.2796 28.8727 89.8387C23.2136 87.3979 18.291 84.0854 14.105 79.9012C9.9188 75.7169 6.60477 70.7965 4.16286 65.1399C1.72096 59.4835 0.5 53.4396 0.5 47.0082C0.5 40.5768 1.72042 34.5316 4.16127 28.8727C6.60212 23.2136 9.91464 18.291 14.0988 14.105C18.2831 9.91881 23.2035 6.60478 28.8601 4.16287C34.5165 1.72096 40.5604 0.5 46.9918 0.5C53.4232 0.5 59.4684 1.72043 65.1273 4.16128C70.7864 6.60213 75.709 9.91464 79.895 14.0988C84.0812 18.2831 87.3952 23.2035 89.8371 28.8601C92.279 34.5165 93.5 40.5604 93.5 46.9918C93.5 53.4232 92.2796 59.4683 89.8387 65.1273C87.3979 70.7864 84.0854 75.709 79.9012 79.895C75.7169 84.0812 70.7965 87.3952 65.1399 89.8371C59.4835 92.279 53.4396 93.5 47.0082 93.5ZM47 86.158C57.9316 86.158 67.1908 82.3646 74.7777 74.7777C82.3646 67.1908 86.158 57.9316 86.158 47C86.158 36.0684 82.3646 26.8092 74.7777 19.2223C67.1908 11.6354 57.9316 7.842 47 7.842C36.0684 7.842 26.8092 11.6354 19.2223 19.2223C11.6354 26.8092 7.842 36.0684 7.842 47C7.842 57.9316 11.6354 67.1908 19.2223 74.7777C26.8092 82.3646 36.0684 86.158 47 86.158Z"
						fill="black" fill-opacity="0.2" />
				</svg>

				<p class="modal__update">Company Name was successfully added.</p>
				<div class="modal__update_btn">
					<div class="btn__wrap">
						<button class="login__btn btn__img btn__modal-nav" type="button" @click="realoarGroup">
							<span class="material-symbols-outlined">add</span>
							Add New User
						</button>
						<button class="login__btn btn__img btn__modal-nav" type="button" @click="openGroup">
							<span class="material-symbols-outlined">corporate_fare</span>
							View Company
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>

	<div v-else-if="modalStore.isModalVisible('step6')" class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">corporate_fare</span>
					<span>Company Onboarding</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<svg class="modal__update_svg" width="100" height="100" viewBox="0 0 100 100" fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						d="M49.9429 79.0138C51.4181 79.0138 52.6642 78.5045 53.681 77.4859C54.6977 76.4673 55.206 75.2204 55.206 73.7451C55.206 72.2699 54.6967 71.0239 53.6781 70.0072C52.6595 68.9904 51.4126 68.482 49.9373 68.482C48.4621 68.482 47.2161 68.9913 46.1994 70.0099C45.1826 71.0285 44.6742 72.2754 44.6742 73.7507C44.6742 75.2259 45.1835 76.472 46.2021 77.4888C47.2207 78.5055 48.4676 79.0138 49.9429 79.0138ZM50.0084 99.0844C43.2197 99.0844 36.8387 97.7962 30.8653 95.2197C24.8919 92.6433 19.6959 89.1467 15.2773 84.7301C10.8585 80.3134 7.36041 75.1196 4.78285 69.1488C2.20529 63.1782 0.916504 56.7985 0.916504 50.0098C0.916504 43.2212 2.20473 36.8402 4.78117 30.8668C7.35762 24.8934 10.8542 19.6973 15.2708 15.2787C19.6875 10.86 24.8813 7.36188 30.852 4.78432C36.8227 2.20676 43.2024 0.917969 49.9911 0.917969C56.7797 0.917969 63.1607 2.2062 69.1341 4.78264C75.1075 7.35909 80.3036 10.8556 84.7222 15.2723C89.1409 19.689 92.639 24.8827 95.2166 30.8535C97.7941 36.8242 99.0829 43.2039 99.0829 49.9925C99.0829 56.7812 97.7947 63.1622 95.2183 69.1355C92.6418 75.109 89.1453 80.305 84.7286 84.7236C80.3119 89.1423 75.1182 92.6405 69.1474 95.218C63.1767 97.7956 56.797 99.0844 50.0084 99.0844ZM49.9997 91.3345C61.5386 91.3345 71.3122 87.3304 79.3205 79.322C87.3289 71.3137 91.3331 61.5401 91.3331 50.0012C91.3331 38.4623 87.3289 28.6887 79.3205 20.6803C71.3122 12.672 61.5386 8.66784 49.9997 8.66784C38.4608 8.66784 28.6872 12.672 20.6789 20.6803C12.6705 28.6887 8.66638 38.4623 8.66638 50.0012C8.66638 61.5401 12.6705 71.3137 20.6789 79.322C28.6872 87.3304 38.4608 91.3345 49.9997 91.3345ZM50.4182 27.8838C52.802 27.8838 54.865 28.6343 56.6071 30.1351C58.3492 31.6359 59.2203 33.5119 59.2203 35.7631C59.2203 37.6576 58.6639 39.3533 57.5511 40.8502C56.4382 42.3473 55.1665 43.7416 53.7357 45.0333C51.8602 46.6971 50.209 48.5272 48.7819 50.5238C47.355 52.5203 46.5918 54.744 46.4924 57.1949C46.4593 58.1354 46.7905 58.9236 47.486 59.5595C48.1815 60.1954 48.9929 60.5133 49.9203 60.5133C50.9138 60.5133 51.755 60.1821 52.4439 59.5197C53.1327 58.8574 53.5732 58.0459 53.7653 57.0855C54.1098 55.3103 54.8434 53.7288 55.9662 52.3412C57.0889 50.9534 58.3052 49.6338 59.615 48.3825C61.4979 46.5471 63.1415 44.5459 64.5457 42.3788C65.95 40.2116 66.6521 37.7945 66.6521 35.1272C66.6521 31.0337 65.0392 27.6704 61.8134 25.0374C58.5876 22.4044 54.8219 21.0879 50.5164 21.0879C47.4429 21.0879 44.545 21.7685 41.8226 23.1297C39.1002 24.4909 36.9805 26.4764 35.4636 29.0863C34.9933 29.8877 34.8423 30.7313 35.0107 31.617C35.179 32.5027 35.6336 33.1752 36.3743 33.6345C37.3171 34.1593 38.2853 34.3091 39.2789 34.0838C40.2724 33.8587 41.117 33.2924 41.8125 32.3849C42.8591 31.0269 44.1259 29.9373 45.613 29.116C47.1 28.2945 48.7018 27.8838 50.4182 27.8838Z"
						fill="#FF3F6D" />
				</svg>
				<p class="modal__update modal__update-accept">Are you sure?</p>
				<p class="modal__update_text">You are about to close this page without completing the<br> onboarding process.
					Your work will not be saved.</p>
				<div class="modal__update_btn">
					<button class="btn btn--white btn--border" type="button" @click="modalStore.closeModal">Close</button>
					<button class="btn" type="submit" @click="goBackToPreviousStep">Continue</button>
				</div>
			</div>
		</div>
	</div>
</template>


<style scoped>
	.login__item_ico{
		top: 50%;
	}
	.modal--profile .modal__body{
		max-width: 615px;
	}

	.btn__wrap{
		display: flex;
		justify-content: center;
		gap: 10px;
	}
	.btn__wrap button {
		width: 160px;
		margin: 0;
	}
	.btn--width{
		min-width: 160px;
	}
	.btn--white{
		border: 1px solid #E2E8F0;
		background: none;
		color:  #0F172A
	}

	.login__btn{
		box-shadow: none;
		min-width: 160px;
	}
	.btn__img{
		gap: 10px;
	}
	.modal__update{
		font-size: 16px;
		margin-bottom: 30px;
	}
	.modal__update-accept{
		margin-bottom: 12px;
	}
	.modal__update_text{
		line-height: 25px;
		margin-bottom: 32px;
	}
	.btn__modal-nav{
		min-width: 200px;
	}
</style>

